<template>
  <div class="split-panel-dark brand-selector">
    <!-- Header -->
    <div class="brand-selector-header">
      <p class="label-section label-section-dark">{{ t('vehicle.step') }}</p>
      <h2 class="heading-hero text-light">{{ t('vehicle.selectBrand') }}</h2>
    </div>

    <!-- Brand roller: 3 copies for infinite loop, middle copy is canonical -->
    <div class="brand-list-wrapper">
      <div ref="scrollRef" class="brand-list-scroll" @scroll="handleScroll">
        <template v-for="(_, setIndex) in 3" :key="setIndex">
          <button
            v-for="brand in sortedBrands"
            :key="`${setIndex}-${brand.id}`"
            :ref="(el) => { if (el && setIndex === 1) brandRefs[brand.id] = el as HTMLElement }"
            @click="handleBrandClick(brand)"
            class="brand-item"
            :class="{
              'brand-item-active': selectedBrand?.id === brand.id,
              'brand-item-disabled': !brand.active
            }"
            :disabled="!brand.active"
          >
            <div class="brand-item-left">
              <span class="brand-item-icon">
                <img v-if="brand.logo" :src="brand.logo" :alt="brand.name" />
                <span v-else v-html="BRAND_FALLBACK_SVG[brand.id] || ''" class="brand-item-icon-svg"></span>
              </span>
              <span class="brand-item-name">{{ brand.name }}</span>
            </div>
            <span v-if="!brand.active" class="brand-item-badge">{{ t('brands.comingSoon') }}</span>
            <svg
              v-else-if="selectedBrand?.id === brand.id"
              class="brand-item-arrow"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </template>
      </div>
    </div>

    <!-- Footer Info -->
    <div class="info-row">
      <div>
        <p class="info-label">{{ t('vehicle.database') }}</p>
        <p class="info-value">{{ activeBrandCount }} {{ t('vehicle.brandsAvailable') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Brand } from '@/data/vehicles.types';

const ROW_HEIGHT = 48;

const BRAND_FALLBACK_SVG: Record<string, string> = {
  mercedes: '<svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="17" stroke="white" stroke-width="2"/><path d="M20 20L20 5M20 20L33 27.5M20 20L7 27.5" stroke="white" stroke-width="1.8" stroke-linecap="round"/><circle cx="20" cy="20" r="2.2" fill="white"/></svg>',
  rivian: '<svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 2L38 20L20 38L2 20Z" stroke="white" stroke-width="2" stroke-linejoin="round"/><path d="M13 13h9c2.8 0 5 2.2 5 5s-2.2 5-5 5h-9V13z" stroke="white" stroke-width="1.8" fill="none"/><path d="M22 23l5 5" stroke="white" stroke-width="1.8" stroke-linecap="round"/></svg>',
};

const { t } = useI18n();

interface Props {
  brands: Brand[];
  selectedBrand: Brand | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'select', brand: Brand): void;
}>();

const scrollRef = ref<HTMLElement | null>(null);
const brandRefs = reactive<Record<string, HTMLElement>>({});
let isResetting = false;

const sortedBrands = computed(() => {
  return [...props.brands].sort((a, b) => {
    if (a.active && !b.active) return -1;
    if (!a.active && b.active) return 1;
    return 0;
  });
});

const oneSetHeight = computed(() => sortedBrands.value.length * ROW_HEIGHT);

const activeBrandCount = computed(() => props.brands.filter(b => b.active).length);

const handleScroll = () => {
  if (isResetting) return;
  const el = scrollRef.value;
  if (!el) return;
  const setH = oneSetHeight.value;

  if (el.scrollTop < setH * 0.5) {
    isResetting = true;
    el.style.scrollBehavior = 'auto';
    el.scrollTop += setH;
    el.style.scrollBehavior = '';
    isResetting = false;
  } else if (el.scrollTop > setH * 1.5) {
    isResetting = true;
    el.style.scrollBehavior = 'auto';
    el.scrollTop -= setH;
    el.style.scrollBehavior = '';
    isResetting = false;
  }
};

const scrollToCenter = (brandId: string, smooth = true) => {
  nextTick(() => {
    const el = scrollRef.value;
    const brandEl = brandRefs[brandId];
    if (!el || !brandEl) return;

    const wrapperH = el.clientHeight;
    const targetTop = brandEl.offsetTop - (wrapperH / 2) + (ROW_HEIGHT / 2);

    if (smooth) {
      el.scrollTo({ top: targetTop, behavior: 'smooth' });
    } else {
      el.style.scrollBehavior = 'auto';
      el.scrollTop = targetTop;
      el.style.scrollBehavior = '';
    }
  });
};

const handleBrandClick = (brand: Brand) => {
  if (brand.active) {
    emit('select', brand);
    scrollToCenter(brand.id);
  }
};

watch(() => props.selectedBrand, (brand) => {
  if (brand) scrollToCenter(brand.id);
});

onMounted(() => {
  // Center on selected brand, or first active brand, or first brand.
  // Delay slightly so the parent layout is fully settled before measuring.
  const targetBrand = props.selectedBrand
    || sortedBrands.value.find(b => b.active)
    || sortedBrands.value[0];

  setTimeout(() => {
    if (targetBrand) {
      scrollToCenter(targetBrand.id, false);
    } else {
      const el = scrollRef.value;
      if (el) {
        el.style.scrollBehavior = 'auto';
        el.scrollTop = oneSetHeight.value;
        el.style.scrollBehavior = '';
      }
    }
  }, 60);
});
</script>

<style scoped>
.brand-item-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.brand-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  opacity: 0.7;
}

.brand-item-icon img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.brand-item-icon-svg :deep(svg) {
  width: 24px;
  height: 24px;
}

.brand-item-active .brand-item-icon {
  opacity: 1;
}
</style>
